import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { LightboxShell } from '@/components/LightboxShell';
import { useLibraryDateFormat } from '@/features/assets/libraryDate';
import { pointsExpiryDisplay } from '@/lib/pointsExpiryDisplay';
import { services } from '@/services';
import { useCatalogStore } from '@/stores/catalogStore';
import { useGroupContextStore } from '@/stores/groupContextStore';
import { useWalletStore } from '@/stores/walletStore';

import { mapRedeemErrorMessage } from './redeemErrorMap';
import { estimateImageCount } from './redeemEstimate';

/**
 * RedeemCodeDialog — 使用者輸入序號兌換點數(R276 建;R277 對帳 docs/99)
 * -----------------------------------------------------------------------------
 * ## 三條拍板
 * 1. 🔴 **點數存入「當下工作區」錢包,切錯工作區無轉移**(owner:「切錯是用戶的問題」)
 *    → 兌換介面**顯眼標示**「將存入:〈當前工作區名〉」;成功畫面用回應的 `group_id`
 *    反查名稱再確認一次(萬一 header 與畫面認知不同步,以回應為準)。
 * 2. **成功畫面不顯「+N 點」數字**(R164/R198)—— 顯「約可再生成 X 張圖片」換算
 *    (`estimateImageCount`;catalog 拿不到就少這一行,不編數字)。
 * 3. **成功後立刻刷錢包**(`GET /me` → walletStore)—— 帳號選單「訂閱與用量」跟著動。
 *
 * ## 冪等(R277 對帳後**刻意沒有** idempotency key)
 * BE 的冪等語意是 per (user × code):同一人重送同一碼 = 200 + 首次結果,不重複入帳
 * (docs/99 §0-2 明令「不要自己做防重」)。所以逾時/失敗就直接重按 —— 不彈「可能重複
 * 兌換」警告,也不用鑄鍵。輸入容錯(dash / 小寫 / 空白 / O→0)也在 BE,FE 原樣送 trim。
 *
 * ## R393(CIS v1.8)
 * 外框走全站 `LightboxShell`(`narrow`):ESC / 點遮罩 / 右上關閉鈕三種出口(原本只有 ESC);
 * **送出中三種出口都鎖住**(半路關窗使用者會不知道到底兌了沒 —— 原本 ESC 那條的語意)。
 * 效期日期走 CIS 寫法(`11月 22, 2026`;每個日期都要有年,原本分鐘級只顯 `M/D`)。
 */

/**
 * 台北日字串 `YYYY-MM-DD` → CIS 日期寫法(`8月 3, 2026`)。正規式拆欄位、**不過 `Date`**
 * (R391:西半球時區會把日期挪一天);拆不開 → 原樣顯示(不偽造日期)。
 */
export function useTaipeiDayFormat(): (ymd: string) => string {
  const { fmtFields } = useLibraryDateFormat();
  return useCallback(
    (ymd: string): string => {
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
      return m ? fmtFields({ y: Number(m[1]), m: Number(m[2]), d: Number(m[3]), hh: '00', mm: '00' }) : ymd;
    },
    [fmtFields],
  );
}

export function RedeemCodeDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('redeem');
  const { t: tc } = useTranslation('common');
  const fmtDay = useTaipeiDayFormat();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    credits: number;
    groupId: string;
    pointsExpireOn: string | null;
    pointsExpireAt: string | null;
  } | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // 「將存入哪個工作區」—— transport 送的是 currentGroupId(X-Current-Group),
  // 名稱從 availableGroups 反查;查不到(理論上不會)退回顯示 id,不顯空白。
  const currentGroupId = useGroupContextStore((s) => s.currentGroupId);
  const availableGroups = useGroupContextStore((s) => s.availableGroups);
  const groupName = (gid: string | null): string | null => {
    if (!gid) return null;
    return availableGroups.find((g) => g.gid === gid)?.name ?? gid;
  };

  // 換算分母要 catalog;開窗即載(store 內建 dedupe,已 ready 是 no-op)。
  const catalog = useCatalogStore((s) => s.catalog);
  const loadCatalog = useCatalogStore((s) => s.load);
  useEffect(() => {
    void loadCatalog();
  }, [loadCatalog]);

  // 開窗聚焦序號欄。⚠️ 本元件是 LightboxShell 的**父層** → 這支 effect 在外框「聚焦關閉鈕」
  // 之後才跑,所以最後焦點在輸入欄(子層 effect 先跑會被外框蓋掉,R293 那條)。
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // 送出中三種出口都不關(半路關窗使用者會不知道到底兌了沒)。
  const guardedClose = () => {
    if (!busy) onClose();
  };

  const trimmed = code.trim();

  const submit = async () => {
    if (busy || !trimmed) return;
    setBusy(true);
    setError(null);
    try {
      const r = await services.redeemCodesService.redeem(trimmed);
      setResult({
        credits: r.credits,
        groupId: r.groupId,
        pointsExpireOn: r.pointsExpireOn,
        pointsExpireAt: r.pointsExpireAt,
      });
      // 刷錢包:讓帳號選單「訂閱與用量」立刻反映(available 變多 → 已用 % 下降)。
      // 失敗只 warn —— 兌換本身已成功,Firestore wallet onSnapshot 也會推,這裡只是搶先。
      try {
        const me = await services.meService.get();
        if (me.wallet && me.currentGroupId) {
          useWalletStore.getState().setWallet(me.currentGroupId, me.wallet);
        }
      } catch (e) {
        console.warn('[redeem] post-redeem wallet refresh failed', e);
      }
    } catch (e) {
      setError(mapRedeemErrorMessage(e, t));
    } finally {
      setBusy(false);
    }
  };

  const success = result !== null;
  // 「約可再生成 X 張圖片」— 只有算得出來且 ≥1 才顯示(0 張是真話但只會困惑;
  // 分母拿不到〔catalog 未載/無 IMAGE〕就少這一行,不硬編價格)。
  const imageCount = result ? estimateImageCount(catalog, result.credits) : null;
  // R391.2 — 效期顯示形狀走全站單點(台北午夜 = 天級顯日期;否則分鐘級顯 M/D HH:mm)。
  const expiry = result ? pointsExpiryDisplay(result.pointsExpireAt, result.pointsExpireOn) : null;

  return (
    <LightboxShell
      ariaLabel={success ? t('dialog.successTitle') : t('dialog.title')}
      head={<h2 style={titleStyle}>{success ? t('dialog.successTitle') : t('dialog.title')}</h2>}
      closeTitle={tc('actions.close')}
      onClose={guardedClose}
      size="narrow"
      backdropClose={!busy}
      data-testid="redeem-dialog"
    >
      {success ? (
        <div data-testid="redeem-success">
          {/* 用**回應的** group_id 說「存進了哪裡」(不是畫面上以為的那個)。 */}
          <p style={{ ...bodyStyle, marginTop: 0 }} data-testid="redeem-success-group">
            {t('dialog.successBody', { group: groupName(result.groupId) ?? result.groupId })}
          </p>
          {/* R391 — 這批點數的效期。null(舊碼 / 舊 BE)整行不渲染 —— 沒有效期就是
              永久,不編一個日期出來;天級顯日期、分鐘級顯到時分(R391.2 單點判準)。
              R393 — 日期一律 CIS 寫法(帶年;分鐘級原本只顯 M/D)。 */}
          {expiry && (
            <p style={expiresStyle} data-testid="redeem-expires">
              {expiry.kind === 'datetime'
                ? t('dialog.expiresOnMinute', { date: fmtDay(expiry.date), time: expiry.time })
                : t('dialog.expiresOn', { date: fmtDay(expiry.date) })}
            </p>
          )}
          {imageCount !== null && imageCount >= 1 && (
            <p style={estimateStyle} data-testid="redeem-estimate">
              {t('dialog.estimateImages', { count: imageCount })}
            </p>
          )}
          <div style={footerStyle}>
            <button
              type="button"
              onClick={onClose}
              style={primaryBtnStyle}
              data-testid="redeem-close"
            >
              {t('dialog.done')}
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* 🔴 owner 拍板:切錯工作區點會進錯錢包且**無轉移** → 目的地必須顯眼,
              不是埋在副標裡的一句話。 */}
          <div style={{ ...groupBannerStyle, marginTop: 0 }} data-testid="redeem-group-banner">
            <span style={groupBannerLabelStyle}>{t('dialog.targetLabel')}</span>
            <span style={groupBannerNameStyle}>
              {groupName(currentGroupId) ?? t('dialog.targetUnknown')}
            </span>
          </div>
          <p style={bodyStyle}>{t('dialog.subtitle')}</p>
          <input
            ref={inputRef}
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              setError(null);
            }}
            onKeyDown={(e) => {
              // IME 確認候選字的 Enter 不當送出(R247.2 教訓)。
              if (e.key === 'Enter' && !e.nativeEvent.isComposing) void submit();
            }}
            placeholder={t('dialog.placeholder')}
            disabled={busy}
            style={inputStyle}
            data-testid="redeem-input"
            spellCheck={false}
            autoComplete="off"
          />
          {error && (
            <p role="alert" style={errorStyle} data-testid="redeem-error">
              {error}
            </p>
          )}
          <div style={footerStyle}>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              style={
                busy ? { ...ghostBtnStyle, ...disabledBtnStyle, border: '1px solid transparent' } : ghostBtnStyle
              }
              data-testid="redeem-cancel"
            >
              {t('dialog.cancel')}
            </button>
            <button
              type="button"
              onClick={() => void submit()}
              disabled={busy || !trimmed}
              // 停用 = CIS 停用色(不用 opacity 調淡)
              style={busy || !trimmed ? { ...primaryBtnStyle, ...disabledBtnStyle } : primaryBtnStyle}
              data-testid="redeem-submit"
            >
              {busy ? t('dialog.submitting') : t('dialog.submit')}
            </button>
          </div>
        </>
      )}
    </LightboxShell>
  );
}

const disabledBtnStyle: CSSProperties = {
  background: 'var(--btn-disabled-bg)',
  color: 'var(--btn-disabled-fg)',
  cursor: 'not-allowed',
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: 16,
  fontWeight: 600,
  color: 'var(--text-strong)',
};

const groupBannerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 8,
  marginTop: 12,
  padding: '9px 12px',
  borderRadius: 'var(--r-btn)',
  background: 'var(--tide-soft)',
  border: '1px solid var(--tide-200)',
};

const groupBannerLabelStyle: CSSProperties = {
  flexShrink: 0,
  fontSize: 11.5,
  fontWeight: 600,
  color: 'var(--concept-fg)',
};

const groupBannerNameStyle: CSSProperties = {
  minWidth: 0,
  fontSize: 13,
  fontWeight: 700,
  color: 'var(--concept-fg)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const bodyStyle: CSSProperties = {
  margin: '10px 0 0',
  fontSize: 12.5,
  lineHeight: 1.6,
  color: 'var(--ink-300)',
};

// teal 文字一律 --concept-fg(--tide-600 在淺底不到 4.5:1)
const estimateStyle: CSSProperties = {
  margin: '10px 0 0',
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--concept-fg)',
};

// 效期提醒:前景走會隨主題翻的 700 階(R266 —— `--warn` 本體只能當淡底,不能當文字)。
const expiresStyle: CSSProperties = {
  margin: '10px 0 0',
  fontSize: 12.5,
  lineHeight: 1.6,
  fontWeight: 600,
  color: 'var(--amber-700)',
};

const inputStyle: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  marginTop: 14,
  padding: '10px 12px',
  borderRadius: 'var(--r-btn)',
  border: '1px solid var(--hairline-strong)',
  background: 'var(--ink-950)',
  color: 'var(--ink-100)',
  fontSize: 14,
  fontFamily: 'var(--font-mono)',
  letterSpacing: '0.04em',
};

// 錯誤 = 玫瑰(--danger-* 是紅,不在 CIS 語意色裡;rose-700 深色主題會翻亮)
const errorStyle: CSSProperties = {
  margin: '8px 0 0',
  fontSize: 11.5,
  lineHeight: 1.5,
  color: 'var(--rose-700)',
};

const footerStyle: CSSProperties = {
  display: 'flex',
  gap: 10,
  justifyContent: 'flex-end',
  marginTop: 20,
};

// CIS v1.8:實心底 --btn-solid(主色 #0D9488 配白字只有 3.74:1)
const primaryBtnStyle: CSSProperties = {
  padding: '8px 18px',
  borderRadius: 'var(--r-btn)',
  border: 'none',
  background: 'var(--btn-solid)',
  color: 'var(--on-accent)',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const ghostBtnStyle: CSSProperties = {
  padding: '8px 18px',
  borderRadius: 'var(--r-btn)',
  border: '1px solid var(--hairline-strong)',
  background: 'transparent',
  color: 'var(--ink-300)',
  fontSize: 13,
  cursor: 'pointer',
};
